import { Modal } from '@mui/material';

const AddressLine = ({ label, value, changed }) => (
  <div className={`text-sm ${changed ? 'font-semibold text-secondary-700' : 'text-gray-700'}`}>
    <span className="text-gray-500 text-xs">{label}: </span>
    {value || '-'}
  </div>
);

const AddressBlock = ({ title, address, changedFields }) => (
  <div className="flex-1 border rounded-lg p-4 bg-gray-50">
    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">{title}</p>
    <AddressLine label="Street" value={address?.street} changed={changedFields?.street} />
    {address?.street2 && (
      <AddressLine label="Street 2" value={address.street2} changed={changedFields?.street2} />
    )}
    <AddressLine label="City" value={address?.city} changed={changedFields?.city} />
    <AddressLine label="State" value={address?.state} changed={changedFields?.state} />
    <AddressLine label="ZIP" value={address?.zip} changed={changedFields?.zip} />
    {address?.zip4 && (
      <AddressLine label="ZIP+4" value={address.zip4} changed={true} />
    )}
  </div>
);

const getChangedFields = (original, suggested) => {
  if (!original || !suggested) return {};
  return {
    street: original.street?.toUpperCase() !== suggested.street?.toUpperCase(),
    street2: (original.street2 || '') !== (suggested.street2 || ''),
    city: original.city?.toUpperCase() !== suggested.city?.toUpperCase(),
    state: original.state?.toUpperCase() !== suggested.state?.toUpperCase(),
    zip: original.zip !== suggested.zip,
  };
};

const AddressValidationDialog = ({
  dialogState,
  onAcceptSuggestion,
  onKeepOriginal,
  onSaveAnyway,
  onCancel,
}) => {
  if (!dialogState?.type) return null;

  const { type, originalAddress, suggestedAddress } = dialogState;
  const changedFields = type === 'suggest' ? getChangedFields(originalAddress, suggestedAddress) : {};

  return (
    <Modal open={true} onClose={onCancel}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full md:w-[550px] max-w-lg border border-gray-200 relative">
          {/* Close button */}
          <button
            type="button"
            onClick={onCancel}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold p-1"
            aria-label="Close"
          >
            &times;
          </button>

          {type === 'suggest' && (
            <>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Address Correction</h3>
              <p className="text-sm text-gray-500 mb-4">
                USPS suggested a corrected address. Would you like to use it?
              </p>

              <div className="flex gap-4 mb-6">
                <AddressBlock
                  title="You entered"
                  address={originalAddress}
                  changedFields={{}}
                />
                <AddressBlock
                  title="Suggested"
                  address={suggestedAddress}
                  changedFields={changedFields}
                />
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onKeepOriginal}
                  className="border border-secondary-700 text-secondary-700 py-2 px-5 rounded-full hover:bg-gray-50 transition text-sm"
                >
                  Keep My Address
                </button>
                <button
                  type="button"
                  onClick={onAcceptSuggestion}
                  className="bg-secondary-700 text-white py-2 px-5 rounded-full shadow hover:bg-secondary-800 transition text-sm"
                >
                  Use Suggested Address
                </button>
              </div>
            </>
          )}

          {type === 'warning' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Address Not Verified</h3>
                  <p className="text-sm text-gray-500">
                    We couldn't verify this address with USPS. It may still be correct.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <AddressBlock title="Your address" address={originalAddress} changedFields={{}} />
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onCancel}
                  className="border border-secondary-700 text-secondary-700 py-2 px-5 rounded-full hover:bg-gray-50 transition text-sm"
                >
                  Go Back and Edit
                </button>
                <button
                  type="button"
                  onClick={onSaveAnyway}
                  className="bg-yellow-500 text-white py-2 px-5 rounded-full shadow hover:bg-yellow-600 transition text-sm"
                >
                  Save Anyway
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddressValidationDialog;
